<script setup lang="ts">
import { inject, ref } from 'vue';
import { DOC_TYPE_LABELS } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { SurveyHistoryStatus } from '@/types';
import { createWorkbookWithSheet, downloadWorkbook } from '@/utils/excel';
import { surveyHistorySourcesKey } from '@/utils/injectionKeys';

const props = defineProps<{
  status: SurveyHistoryStatus;
}>();

const tableContainer = ref<HTMLDivElement | null>(null);
const injectedSourceUrls = inject(surveyHistorySourcesKey);

const EXCEL_FULL_CHAR_WIDTH = 2.2; // 全角文字のExcelの1文字あたりの列幅
const EXCEL_HALF_CHAR_WIDTH = 1; // 半角文字のExcelの1文字あたりの列幅

/**
 * Excelにおける文字列の列幅を計算する
 *
 * NOTE: char.charCodeAtを255との比較で全角/半角の判定をしているのは、実際には厳密ではないが列幅の調整には問題ない
 * NOTE: ExcelJSのColumn.widthは標準のフォントサイズ（通常は11ポイント）で表示できる文字数だが、ダウンロードファイルをOffice365のExcelで開くと
 *       文字数に対して列幅が足りなかったので、全角は2.2で調節する（調整したところ全角は2.2がちょうど良い感じの列幅になった）
 */
const getEffectiveWidth = (text: string): number => {
  return text.split('').reduce((totalWidth, char) => {
    return (
      totalWidth +
      (char.charCodeAt(0) > 255 ? EXCEL_FULL_CHAR_WIDTH : EXCEL_HALF_CHAR_WIDTH)
    );
  }, 0);
};

const downloadExcel = async () => {
  const table = tableContainer.value?.querySelector('table');
  if (!table) return;

  const { workbook, worksheet } = await createWorkbookWithSheet({
    sheetName: 'Sheet1',
    freezeHeaderRow: false,
    fitToPage: false,
  });

  const maxColumnWidths: Array<number> = []; // 各列の最大幅

  const docTypeLabels = Object.values(DOC_TYPE_LABELS);
  const docTypePattern = docTypeLabels.join('|');
  const regex = new RegExp(`(\\d+)\\.(${docTypePattern})`, 'g');

  table.querySelectorAll('tr').forEach(tr => {
    const rowData: Array<string> = [];
    tr.querySelectorAll('th, td').forEach((cell, colIdx) => {
      let text = cell.textContent?.trim() || '';
      text = text.replace(regex, (_match, num) => `[${num}]`);
      rowData.push(text);

      // 各列幅の調整用に最大幅を計算
      const effectiveWidth = getEffectiveWidth(text);
      maxColumnWidths[colIdx] = Math.max(
        maxColumnWidths[colIdx] ?? 0,
        effectiveWidth,
      );
    });
    worksheet.addRow(rowData);
  });

  const LIMIT_WIDTH = 100;
  worksheet.columns = maxColumnWidths.map(maxColumnWidth => {
    return { width: Math.min(maxColumnWidth, LIMIT_WIDTH) };
  });

  // 表とソースの間に空行を追加
  worksheet.addRow([]);

  // ソースリスト（引用番号とURL）を追加
  if (injectedSourceUrls?.value && injectedSourceUrls.value.length > 0) {
    worksheet.addRow(['ソース']);
    injectedSourceUrls.value.forEach((url: string, index: number) => {
      worksheet.addRow([`[${index + 1}]`, url]);
    });
  }

  await downloadWorkbook(workbook, {
    filename: 'table_data.xlsx',
    format: 'excel',
  });
};
</script>

<template>
  <div class="survey-table-wrapper">
    <div ref="tableContainer">
      <slot />
    </div>
  </div>
  <div class="table-footer" v-if="!props.status.isInProgress">
    <button
      @click="downloadExcel"
      class="c-btn c-title-bold--m download-button"
    >
      <DgrIcon name="download" size="small" class="download-icon" />
      xlsx形式でダウンロード
    </button>
  </div>
</template>

<style lang="scss" scoped>
.survey-table-wrapper {
  overflow-x: auto;
}
.table-footer {
  padding: 8px;
}

.download-button {
  gap: 4px;
  width: 100%;
  max-width: 216px;
  padding: 5px 16px;
  border: none;
}

.download-icon {
  flex-shrink: 0;
}
</style>
